// ============================================================================
// BREAK-GLASS GATE
// Wraps a project or session route. For System Administrators, a 403 from the
// resource (or navigation state flagging a resource they do not own) replaces
// the page with a break-glass prompt; once granted, the page remounts so every
// load runs again with the X-Break-Glass-* headers. Other users pass straight
// through, unchanged.
// ============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { BreakGlassModal } from '../common/BreakGlassModal';
import { BreakGlassBanner } from './BreakGlassBanner';
import { useBreakGlass, useBreakGlassDenied, useIsSystemAdmin } from '../../contexts/BreakGlassContext';
import { BreakGlassScope } from '../../services/breakGlass';

export const BREAK_GLASS_REQUIRED_STATE = 'required';

interface BreakGlassGateProps {
  type: 'project' | 'session';
  idParam: string;
  children: React.ReactNode;
}

const LABELS = { project: 'project', session: 'session' } as const;

export const BreakGlassGate: React.FC<BreakGlassGateProps> = ({ type, idParam, children }) => {
  const params = useParams();
  const id = params[idParam];
  const location = useLocation();
  const navigate = useNavigate();
  const isSystemAdmin = useIsSystemAdmin();

  const scope = useMemo<BreakGlassScope | null>(
    () => (isSystemAdmin && id ? { type, id } : null),
    [isSystemAdmin, id, type]
  );
  const { grant, start, end } = useBreakGlass(scope);
  const [denial, resetDenial] = useBreakGlassDenied(scope);
  const [modalOpen, setModalOpen] = useState(false);
  const [epoch, setEpoch] = useState(0);

  const preflight = (location.state as any)?.breakGlass === BREAK_GLASS_REQUIRED_STATE;
  const needsGrant = !!scope && !grant && (!!denial || preflight);

  useEffect(() => {
    if (needsGrant) setModalOpen(true);
  }, [needsGrant]);

  if (!scope) return <>{children}</>;

  const label = LABELS[type];
  const pathPrefix = type === 'project' ? `/projects/${id}` : location.pathname;

  const handleConfirm = (ctx: { category: string; justification: string }) => {
    start(ctx, { path: pathPrefix });
    resetDenial();
    setModalOpen(false);
    setEpoch(e => e + 1);
  };

  const handleEnd = () => {
    end();
    navigate('/projects');
  };

  const modal = (
    <BreakGlassModal
      isOpen={modalOpen}
      onClose={() => setModalOpen(false)}
      onConfirm={handleConfirm}
      action={`open ${label} '${id}' that you do not own`}
      note="Access is read-only and ends after 30 minutes or when you leave this page."
    />
  );

  if (needsGrant) {
    return (
      <div className="h-screen bg-surface-light flex items-center justify-center font-display p-6">
        <div className="bg-white border border-[#e2e8f0] rounded-2xl p-8 max-w-lg text-center shadow-sm" data-testid="break-glass-required">
          <div className="w-12 h-12 rounded-full bg-amber-50 text-amber-500 flex items-center justify-center mx-auto mb-4">
            <Lock size={22} />
          </div>
          <h1 className="text-lg font-bold text-navy mb-2">You do not have access to this {label}</h1>
          <p className="text-sm text-slate-600 mb-6">
            You are not the owner and are not on its access list. As a System Administrator you can open it
            read-only with break-glass access. Your reason and every request are audited.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => navigate('/projects')}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
            >
              Back to projects
            </button>
            <button
              onClick={() => setModalOpen(true)}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-amber-600 hover:bg-amber-700 transition-colors text-white text-sm font-bold shadow-sm"
            >
              Use break-glass access
            </button>
          </div>
        </div>
        {modal}
      </div>
    );
  }

  return (
    <>
      {grant && (
        <BreakGlassBanner
          floating
          grant={grant}
          onEnd={handleEnd}
          warning={denial?.withBreakGlass ? 'Some data was refused by the server.' : undefined}
        />
      )}
      <React.Fragment key={epoch}>{children}</React.Fragment>
      {modal}
    </>
  );
};
